import ProductIdentity from './ProductIdentity';
import React, { useEffect, useState } from "react";
import api from "../api";

const money = (value) =>
  new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" }).format(
    value / 100,
  );
const labels = {
  SUCCEEDED: "Оплачено",
  REFUNDED: "Возвращено полностью",
  REFUND_PENDING: "Возврат обрабатывается",
  FAILED: "Не получилось оплатить",
  AUTHORIZED: "Сумма зарезервирована",
  PENDING: "Ожидаем оплату",
  UNKNOWN: "Нужна проверка состояния",
  PARTIAL_REFUNDED: "Частичный возврат — требуется сверка",
};

export default function AdminPaymentCheck() {
  const [data, setData] = useState({ products: [], attempts: [] });
  const [variantId, setVariantId] = useState("");
  const [attemptId, setAttemptId] = useState(
    () =>
      new URLSearchParams(window.location.search).get("paymentAttemptId") || "",
  );
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const product = data.products.find(
    (item) => item.variantId === Number(variantId),
  );
  const refresh = async () => {
    const response = await api.get("/api/admin/payment-check");
    setData(response.data);
    setVariantId(
      (previous) =>
        previous || String(response.data.products[0]?.variantId || ""),
    );
  };
  useEffect(() => {
    refresh().catch((e) =>
      setError(
        e.response?.data?.error || "Не удалось загрузить проверку оплаты",
      ),
    );
  }, []);
  const check = async (id) => {
    const response = await api.get(`/api/admin/payment-check/${id}`);
    setState(response.data);
    return response.data;
  };
  useEffect(() => {
    if (!attemptId) return undefined;
    let active = true,
      timer,
      count = 0;
    const tick = async () => {
      try {
        const response = await api.get(`/api/admin/payment-check/${attemptId}`);
        if (!active) return;
        setState(response.data);
        if (
          ["PENDING", "REFUND_PENDING", "UNKNOWN"].includes(
            response.data.status,
          ) &&
          ++count < 12
        )
          timer = setTimeout(tick, 5000);
      } catch (failure) {
        if (active)
          setError(
            failure.response?.data?.error || "Не удалось проверить статус",
          );
      }
    };
    setState(null);
    tick();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [attemptId]);
  const run = async (action) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await action();
      await refresh();
    } catch (failure) {
      setError(
        failure.response?.data?.error ||
          "Операция не завершена. Проверьте статус перед повтором.",
      );
    } finally {
      setBusy(false);
    }
  };
  const pay = () =>
    run(async () => {
      const response = await api.post("/api/admin/payment-check", {
        variantId: product.variantId,
        expectedAmountMinor: product.amountMinor,
      });
      setAttemptId(String(response.data.attemptId));
      window.location.assign(response.data.paymentURL);
    });
  return (
    <section className="admin-payment-check">
      <h2>Проверка интернет-эквайринга</h2>
      <p>
        Рабочий терминал. Оплата на следующем экране спишет реальные деньги.
        Касса и отправка чеков сейчас не подключены. Доставка для этой проверки
        не создаётся.
      </p>
      {error && (
        <p role="alert" className="checkout-notice checkout-notice--error">
          {error}
        </p>
      )}
      <fieldset className="admin-product-picker" disabled={busy}>
        <legend>Товар для проверки</legend>
        {data.products.map(item => <label className="admin-product-option" key={item.variantId}>
          <input type="radio" name="payment-check-product" value={item.variantId} checked={String(item.variantId) === variantId} onChange={() => setVariantId(String(item.variantId))} />
          <ProductIdentity product={item} zoom={false}><small>{item.size} · {money(item.amountMinor)}</small></ProductIdentity>
        </label>)}
      </fieldset>
      {product && (
        <p>
          <strong>К оплате: {money(product.amountMinor)}</strong>
        </p>
      )}
      <button
        className="btn btn-primary"
        type="button"
        disabled={busy || !product}
        onClick={pay}
      >
        {busy ? "Выполняем…" : "Перейти к реальной оплате"}
      </button>
      <h3>Платежи проверки</h3>
      <label htmlFor="payment-check-attempt">Операция</label>
      <select
        id="payment-check-attempt"
        value={attemptId}
        disabled={busy}
        onChange={(event) => setAttemptId(event.target.value)}
      >
        <option value="">Выберите операцию</option>
        {data.attempts.map((item) => (
          <option key={item.id} value={item.id}>
            Заказ №{item.customerOrderId} · {money(item.amountMinor)} ·{" "}
            {item.providerRawStatus || item.status}
          </option>
        ))}
      </select>
      {state && (
        <div role="status" aria-live="polite">
          <h3>{labels[state.status] || "Статус оплаты"}</h3>
          <p>
            Заказ №{state.orderId} · {money(state.amountMinor)}
          </p>
          {state.paymentId && <p>Номер платежа: {state.paymentId}</p>}
          {state.refundStatus && (
            <p>
              Учёт возврата:{" "}
              {state.refundStatus === "SUCCEEDED"
                ? "Подтверждён"
                : "Обрабатывается"}
            </p>
          )}
        </div>
      )}
      <div className="admin-payment-actions">
        <button
          className="btn btn-outline"
          type="button"
          disabled={busy || !attemptId}
          onClick={() => run(() => check(attemptId))}
        >
          Проверить статус
        </button>
        <button
          className="btn btn-outline"
          type="button"
          disabled={
            busy ||
            !state ||
            !["SUCCEEDED", "REFUND_PENDING"].includes(state.status)
          }
          onClick={() =>
            run(async () => {
              const response = await api.post(
                `/api/admin/payment-check/${attemptId}/refund`,
              );
              setState(response.data);
            })
          }
        >
          Вернуть всю сумму{state ? ` ${money(state.amountMinor)}` : ""}
        </button>
        <button
          className="btn btn-outline"
          type="button"
          disabled={
            busy ||
            !attemptId ||
            ["SUCCEEDED", "REFUNDED", "REFUND_PENDING", "AUTHORIZED"].includes(
              state?.status,
            )
          }
          onClick={() =>
            run(async () => {
              const response = await api.post(
                `/api/admin/payment-check/${attemptId}/cancel`,
              );
              setState(response.data);
            })
          }
        >
          Отменить неоплаченную попытку
        </button>
      </div>
    </section>
  );
}
